import { type Column, type RowData } from '@tanstack/react-table';
import * as React from 'react';
import { type RefObject, useLayoutEffect } from 'react';

import { cn } from '../../utils';

import { DataTableRefContext } from './contexts/data-table-ref.context';
import {
  getPinnedDataAttribute,
  getPinnedEdgeDataAttribute,
  getPinnedSideDataAttribute,
  getStickyCellStyles,
} from './utilities/column-pinning';

interface DataTableProps extends React.HTMLAttributes<HTMLTableElement> {
  showScrollbar?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
}

const DataTable = React.forwardRef<HTMLTableElement, DataTableProps>(
  ({ className, containerRef, ...props }, ref) => {
    const tableRef = React.useRef<HTMLTableElement | null>(null);

    React.useImperativeHandle(ref, () => tableRef.current as HTMLTableElement);

    return (
      <div ref={containerRef} className="w-full">
        <DataTableRefContext.Provider value={tableRef}>
          <table
            ref={tableRef}
            className={cn(
              'w-full caption-bottom text-sm border-separate border-spacing-0',
              className,
            )}
            {...props}
          />
        </DataTableRefContext.Provider>
      </div>
    );
  },
);
DataTable.displayName = 'Table';

const DataTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:border-b bg-accent text-sm [&>tr]:first:hover:bg-accent sticky top-0 shadow-sm z-20',
      className,
    )}
    {...props}
  />
));
DataTableHeader.displayName = 'TableHeader';

const DataTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
DataTableBody.displayName = 'TableBody';

const DataTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    role="rowgroup"
    ref={ref}
    className={cn(
      'sticky bottom-0 z-20 text-sm text-muted-foreground py-2 px-4 bg-background',
      className,
    )}
    {...props}
  />
));
DataTableFooter.displayName = 'TableFooter';

interface DataTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  id: string;
  highlight?: boolean;
  onHighlightEnd?: () => void;
}

const DataTableRow = React.forwardRef<HTMLTableRowElement, DataTableRowProps>(
  ({ id, className, onHighlightEnd, highlight, ...props }, ref) => {
    const internalRef = React.useRef<HTMLTableRowElement>(null);

    useLayoutEffect(() => {
      if (highlight) {
        internalRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        const timeoutId = setTimeout(() => {
          onHighlightEnd?.();
        }, 6000);

        return () => clearTimeout(timeoutId);
      }
    }, [highlight, onHighlightEnd]);

    return (
      <tr
        id={id}
        ref={ref || internalRef}
        className={cn(
          'transition-colors bg-paper [&>*]:duration-1000',
          highlight &&
            '[&>*]:bg-primary/10 [&>*]:animate-pulse [&>*]:duration-[2000] [&>*]:transition-colors',
          className,
        )}
        {...props}
      />
    );
  },
);
DataTableRow.displayName = 'TableRow';

interface DataTableHeadProps<TData extends RowData, TValue>
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  id: string;
  column?: Column<TData, TValue>;
}

const _DataTableHead = React.forwardRef<
  HTMLTableCellElement,
  DataTableHeadProps<unknown, unknown>
>(({ id, className, column, style, ...props }, ref) => {
  const stickyHeaderStyles: React.CSSProperties = React.useMemo(
    () => (column ? getStickyCellStyles(column) : {}),
    [column],
  );

  return (
    <th
      id={id}
      ref={ref}
      style={{ ...stickyHeaderStyles, ...style }}
      data-pinned-edge={getPinnedEdgeDataAttribute(column)}
      data-pinned-side={getPinnedSideDataAttribute(column)}
      data-pinned={getPinnedDataAttribute(column)}
      className={cn(
        'px-4 py-3 text-left align-middle font-medium leading-medium bg-accent [&:has([role=checkbox])]:pr-0',
        'data-[pinned-edge="left"]:shadow-left-col data-[pinned-edge="left"]:z-20 data-[pinned-edge="right"]:shadow-right-col data-[pinned-edge="right"]:z-20 data-[pinned="true"]:z-20',
        className,
      )}
      {...props}
    />
  );
});
_DataTableHead.displayName = 'TableHead';

export const DataTableHead = _DataTableHead as <TData extends RowData, TValue>(
  props: DataTableHeadProps<TData, TValue> &
    React.RefAttributes<HTMLTableCellElement>,
) => React.ReactNode;

interface DataTableCellProps<TData extends RowData, TValue>
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  id: string;
  column?: Column<TData, TValue>;
}

const _DataTableCell = React.forwardRef<
  HTMLTableCellElement,
  DataTableCellProps<unknown, unknown>
>(({ id, children, className, column, style, ...props }, ref) => {
  const stickyCellStyles: React.CSSProperties = React.useMemo(
    () => (column ? getStickyCellStyles(column) : {}),
    [column],
  );

  return (
    <td
      id={id}
      ref={ref}
      style={{ ...stickyCellStyles, ...style }}
      data-pinned-edge={getPinnedEdgeDataAttribute(column)}
      data-pinned-side={getPinnedSideDataAttribute(column)}
      data-pinned={getPinnedDataAttribute(column)}
      className={cn(
        'h-[72px] py-3 px-4 align-middle bg-inherit border-b border-accent font-medium',
        'data-[pinned-edge="left"]:shadow-left-col data-[pinned-edge="left"]:z-10 data-[pinned-edge="right"]:shadow-right-col data-[pinned-edge="right"]:z-10 data-[pinned="true"]:z-10',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
});
_DataTableCell.displayName = 'TableCell';

export const DataTableCell = _DataTableCell as <TData extends RowData, TValue>(
  props: DataTableCellProps<TData, TValue> &
    React.RefAttributes<HTMLTableCellElement>,
) => React.ReactNode;

const _DataTableTotalsCell = React.forwardRef<
  HTMLTableCellElement,
  DataTableCellProps<unknown, unknown>
>(({ className, ...props }, ref) => {
  return (
    <_DataTableCell
      ref={ref}
      className={cn('font-semibold', className)}
      {...props}
    />
  );
});
_DataTableTotalsCell.displayName = 'TableTotalsCell';

export const DataTableTotalsCell = _DataTableTotalsCell as <
  TData extends RowData,
  TValue,
>(
  props: DataTableCellProps<TData, TValue> &
    React.RefAttributes<HTMLTableCellElement>,
) => React.ReactNode;

export {
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableFooter,
  DataTableRow,
};
